import { useCallback, useEffect, useRef, useState } from "react";
import { applyAllSpellMatches, applySpellMatch, checkSpanishSpelling } from "@/lib/spellcheckApi";
import {
  buildMatchForWord,
  findMatchCoveringOffset,
  getCaretWordBounds,
  localTypoSuggestions,
  normalizeSpellReplacements,
} from "@/lib/spellcheckCaret";
import { isEmbeddedBrowserContext } from "@/lib/embeddedBrowserContext";
import {
  loadSpellcheckLearned,
  matchWordFromText,
  partitionSpellMatches,
  rememberSpellIgnored,
  rememberSpellReplacement,
} from "@/lib/spellcheckLearnedStore";

async function resolveSpellMatchAtCaret(text, caret, runCheck, minChars) {
  const bounds = getCaretWordBounds(text, caret);
  const word = bounds.word;
  if (!word || word.length < minChars) return null;

  const fullMatches = await runCheck(text);
  let match = findMatchCoveringOffset(fullMatches, caret)
    || findMatchCoveringOffset(fullMatches, bounds.start);
  let suggestions = normalizeSpellReplacements(match);
  if (suggestions.length) return { match, suggestions, bounds };

  if (word !== String(text || "").trim()) {
    const wordMatches = await runCheck(word);
    const wordMatch = wordMatches.find((m) => (Number(m?.offset) || 0) === 0) || wordMatches[0];
    suggestions = normalizeSpellReplacements(wordMatch);
    if (suggestions.length) {
      match = buildMatchForWord(text, bounds, suggestions);
      return { match, suggestions, bounds };
    }
  }

  const local = localTypoSuggestions(word);
  if (local.length && local[0].toLowerCase() !== word.toLowerCase()) {
    match = buildMatchForWord(text, bounds, local);
    return { match, suggestions: local, bounds };
  }

  return null;
}

/**
 * Corrector estilo navegador: revisión silenciosa + sugerencias en clic derecho o al seleccionar.
 * Recuerda correcciones aceptadas / términos ignorados (localStorage) para mensajes repetitivos.
 */
export function useSpellcheckContextMenu({
  value,
  onApply,
  enabled = true,
  minChars = 3,
  debounceMs = 700,
} = {}) {
  const [matches, setMatches] = useState([]);
  const [menu, setMenu] = useState(null);
  const requestIdRef = useRef(0);
  const menuGuardRef = useRef(0);
  const onApplyRef = useRef(onApply);
  const embedded = isEmbeddedBrowserContext();

  useEffect(() => {
    onApplyRef.current = onApply;
  }, [onApply]);

  const processMatches = useCallback((text, nextMatches) => {
    const learned = loadSpellcheckLearned();
    const { toApply, remaining } = partitionSpellMatches(text, nextMatches, learned);
    if (toApply.length) {
      const corrected = applyAllSpellMatches(text, toApply);
      if (corrected !== text) {
        onApplyRef.current?.(corrected);
        setMatches([]);
        return [];
      }
    }
    setMatches(remaining);
    return remaining;
  }, []);

  const runCheck = useCallback(async (text) => {
    const trimmed = String(text || "").trim();
    if (!enabled || trimmed.length < minChars) {
      setMatches([]);
      return [];
    }
    const requestId = ++requestIdRef.current;
    try {
      const result = await checkSpanishSpelling(trimmed);
      if (requestId !== requestIdRef.current) return [];
      const next = Array.isArray(result?.matches) ? result.matches : [];
      return processMatches(String(text || ""), next);
    } catch {
      if (requestId === requestIdRef.current) setMatches([]);
      return [];
    }
  }, [enabled, minChars, processMatches]);

  useEffect(() => {
    if (!enabled) {
      setMatches([]);
      return undefined;
    }
    const text = String(value || "");
    if (text.trim().length < minChars) {
      setMatches([]);
      return undefined;
    }
    const timer = window.setTimeout(() => {
      runCheck(text);
    }, debounceMs);
    return () => window.clearTimeout(timer);
  }, [debounceMs, enabled, minChars, runCheck, value]);

  const closeMenu = useCallback(() => setMenu(null), []);

  const openMenuAt = useCallback((x, y, resolved) => {
    if (!resolved?.suggestions?.length) return;
    menuGuardRef.current = Date.now();
    setMenu({
      x,
      y,
      match: resolved.match,
      suggestions: resolved.suggestions,
      word: resolved.bounds?.word,
    });
  }, []);

  const applySuggestion = useCallback((replacement) => {
    if (!menu?.match) return;
    const word = matchWordFromText(String(value || ""), menu.match) || menu.word;
    rememberSpellReplacement(word, replacement);
    const next = applySpellMatch(String(value || ""), menu.match, replacement);
    onApply?.(next);
    setMatches((prev) => prev.filter((m) => m !== menu.match));
    closeMenu();
  }, [closeMenu, menu, onApply, value]);

  /** Aplica una sugerencia desde la barra de chips (sin menú contextual). */
  const applyMatchSuggestion = useCallback((match, replacement) => {
    if (!match || !replacement) return;
    const word = matchWordFromText(String(value || ""), match);
    rememberSpellReplacement(word, replacement);
    const next = applySpellMatch(String(value || ""), match, replacement);
    onApply?.(next);
    setMatches((prev) => prev.filter((m) => m !== match));
    closeMenu();
  }, [closeMenu, onApply, value]);

  const ignoreMatchSuggestion = useCallback((match) => {
    if (!match) return;
    const word = matchWordFromText(String(value || ""), match);
    rememberSpellIgnored(word);
    setMatches((prev) => prev.filter((m) => m !== match));
    closeMenu();
  }, [closeMenu, value]);

  const showSuggestionsForCaret = useCallback(async (el, caret, anchor) => {
    const text = String(value || "");
    if (text.trim().length < minChars) return false;
    const resolved = await resolveSpellMatchAtCaret(text, caret, runCheck, minChars);
    if (!resolved) return false;
    const rect = el.getBoundingClientRect?.();
    openMenuAt(
      anchor?.x ?? rect?.left ?? 0,
      anchor?.y ?? ((rect?.bottom ?? 0) + 4),
      resolved,
    );
    return true;
  }, [minChars, openMenuAt, runCheck, value]);

  const handleContextMenu = useCallback(async (event) => {
    if (!enabled) return;
    const el = event.currentTarget;
    const caret = el.selectionStart ?? el.selectionEnd ?? 0;
    const shown = await showSuggestionsForCaret(el, caret, {
      x: event.clientX,
      y: event.clientY,
    });
    if (shown) event.preventDefault();
  }, [enabled, showSuggestionsForCaret]);

  const handleMouseUp = useCallback(async (event) => {
    if (!enabled || !embedded) return;
    const el = event.currentTarget;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    if (start === end) return;
    const text = String(value || "");
    const selected = text.slice(start, end);
    if (selected.trim().length < minChars) return;
    await showSuggestionsForCaret(el, start, {
      x: event.clientX,
      y: event.clientY,
    });
  }, [embedded, enabled, minChars, showSuggestionsForCaret, value]);

  useEffect(() => {
    if (!menu) return undefined;
    const onDismiss = (event) => {
      if (Date.now() - menuGuardRef.current < 250) return;
      const root = document.querySelector('[data-spellcheck-context-menu="true"]');
      if (root?.contains(event.target)) return;
      closeMenu();
    };
    window.addEventListener("mousedown", onDismiss);
    window.addEventListener("scroll", onDismiss, true);
    window.addEventListener("resize", onDismiss);
    return () => {
      window.removeEventListener("mousedown", onDismiss);
      window.removeEventListener("scroll", onDismiss, true);
      window.removeEventListener("resize", onDismiss);
    };
  }, [closeMenu, menu]);

  return {
    matches,
    menu,
    hasErrors: matches.length > 0,
    handleContextMenu,
    handleMouseUp,
    closeMenu,
    applySuggestion,
    applyMatchSuggestion,
    ignoreMatchSuggestion,
    useNativeSpellcheck: !embedded,
  };
}
